/**
 * Context-locked palette for the "State of Local Emergency" layer (#213, Advisories
 * & Declarations group). BC State of Local Emergency declarations + Band Council
 * Resolutions from the arcgis_public_features `emergency_declaration` category —
 * municipality / First-Nation AREA polygons, severity the `SOLE_TYPE_DESCRIPTION`.
 *
 * This is the doctrinal "who has DECLARED an emergency" register — a governance
 * state, not a hazard source. It owns a bold MAGENTA / fuchsia identity so a
 * declaration boundary stands out as an administrative overlay distinct from every
 * hazard family (fire brown, flood/CAP blue, evac violet, drought tan) and from the
 * urgent red (Colour Law 5). A full State of Local Emergency reads heavier than a
 * Band Council Resolution (deeper magenta, bolder outline). Colour is always paired
 * with the declaration-type label at the call site.
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { ArcgisLayerStyle } from '../layers/arcgisStyle';

const SOLE: ArcgisLayerStyle = {
  color: '#a21caf', // fuchsia-700 — the bold "emergency declared" register
  fillAlpha: 0.16,
  strokeAlpha: 0.92,
  outlineWidth: 4,
  pointPixelSize: 13,
  label: 'State of Local Emergency',
};

const RESOLUTION: ArcgisLayerStyle = {
  color: '#c13fc9', // a lighter magenta — Band Council Resolution / other declaration
  fillAlpha: 0.14,
  strokeAlpha: 0.88,
  outlineWidth: 3,
  pointPixelSize: 12,
  label: 'Band Council Resolution',
};

const UNKNOWN: ArcgisLayerStyle = {
  // Still a DECLARATION — keep it in the magenta family (a mid magenta) rather than
  // dropping to grey, so an unmapped declaring body still reads as "declared here".
  color: '#b226b8',
  fillAlpha: 0.14,
  strokeAlpha: 0.85,
  outlineWidth: 3,
  pointPixelSize: 12,
  label: 'Emergency declaration',
};

/**
 * Resolve the style for a declaration. The BC SoLE feed publishes the DECLARING-BODY
 * type in `SOLE_TYPE_DESCRIPTION` ("Local Government" / "First Nation") rather than a
 * declaration class, so a municipal/local-government declaration → the bold SoLE
 * register and a First-Nation declaration → the (often Band-Council-Resolution)
 * lighter register; the explicit "State of Local Emergency" / "Band Council
 * Resolution" keywords are also matched. Anything else stays in the magenta family
 * (still a declaration), never dropped.
 */
export function emergencyDeclarationStyle(severity: string | null | undefined): ArcgisLayerStyle {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('first nation') || s.includes('band') || s.includes('resolution') || s.includes('bcr'))
    return RESOLUTION;
  if (
    s.includes('local government') ||
    s.includes('municipal') ||
    s.includes('emergency') ||
    s.includes('sole')
  )
    return SOLE;
  return UNKNOWN;
}
